import cn from 'classnames';
import { useEffect } from 'react';
import { useSuspenseAuditLogs } from '#api/audit-logs.queries.ts';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { CenteredLayout } from '#components/patterns/layouts/centered/CenteredLayout.tsx';
import type { MetadataNoCounts } from '#dn-types/api.ts';
import type { AuditLog } from '#dn-types/audit-log.ts';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { AuditLogRow } from '../row/AuditLogRow.tsx';
import styles from './AuditLogList.module.css';

type Props = {
  className?: string;
  /** These are arguments that are provided to the useAuditLogs query */
  logsQueryArgs: Parameters<typeof useSuspenseAuditLogs>[0];
  /** We need to let the page know when we are fetching, so it can dim the results */
  setIsFetching?: (isFetching: boolean) => void;
  /** Let the page know about the metadata, needed for pagination */
  setMetadata?: (metadata: MetadataNoCounts | undefined) => void;
  /** Let the page know if there are no results */
  setIsEmpty?: (isEmpty: boolean) => void;
  /** Individual entity logs (e.g. HostLogs) only show the first X results */
  selectRows?: (logs: AuditLog[]) => AuditLog[];
  EmptyLogsComponent?: React.ComponentType<EmptyLogsProps>;
};

export function AuditLogList({
  className,
  logsQueryArgs,
  setIsFetching,
  setMetadata,
  setIsEmpty,
  selectRows,
  EmptyLogsComponent = EmptyLogs,
}: Props) {
  const { data: { data: logs, metadata } = {}, isFetching } = useSuspenseAuditLogs(logsQueryArgs);

  useEffect(() => {
    setIsFetching?.(isFetching);
    setMetadata?.(metadata);
    setIsEmpty?.(logs?.length === 0);
  }, [logs, isFetching, metadata, setIsFetching, setMetadata, setIsEmpty]);

  if (!logs) {
    return <LoadingSpinner />;
  }

  const selectedRows = selectRows ? selectRows(logs) : logs;

  const isFiltered = Boolean(logsQueryArgs.targetId) || Boolean(logsQueryArgs.targetType);

  return (
    <div key={selectedRows[0]?.id} className={cn(className, styles.AuditLogList, { 'opacity-dim': isFetching })}>
      {selectedRows.length === 0 ?
        <EmptyLogsComponent isFiltered={isFiltered} />
      : selectedRows.map((log) => <AuditLogRow key={log.id} log={log} />)}
    </div>
  );
}

type EmptyLogsProps = { isFiltered: boolean };

function EmptyLogs({ isFiltered }: EmptyLogsProps) {
  return (
    <CenteredLayout className={styles.AuditLogList___empty}>
      <div>
        <h2 className="h3 mb-16">No results found</h2>
        {isFiltered ?
          <>
            <p className="p-lt mb-24">To see all your audit logs, clear the filters.</p>
            <LinkButton to="/logs/audit" variant="secondary">
              Clear filters
            </LinkButton>
          </>
        : null}
      </div>
    </CenteredLayout>
  );
}
